import { IReactSelectOption } from '../../../utils/types';
import { Editor } from '@tiptap/react';
import { BasicDropdownIndicator } from '../../Dropdown/StyledReactSelect/SelectUtils';
import {
  components as ReactSelectComponents,
  SelectComponentsConfig,
  SingleValue,
  StylesConfig,
} from 'react-select';
import { ReactSelectMenu } from '../../Dropdown/StyledReactSelect';
import { isNil } from 'lodash';
import { theme } from '../../../styles/theme';

const heading1Option: IReactSelectOption = {
  value: 'heading-1',
  label: 'Title',
};

const heading2Option: IReactSelectOption = {
  value: 'heading-2',
  label: 'Subtitle',
};

const heading3Option: IReactSelectOption = {
  value: 'heading-3',
  label: 'Section',
};

const textOption: IReactSelectOption = { value: 'paragraph', label: 'Text' };

const blockStyleOptions = [
  heading1Option,
  heading2Option,
  heading3Option,
  textOption,
];

const getSelectedBlockStyle = (editor: Editor) => {
  if (editor.isActive('paragraph')) {
    return textOption;
  } else if (editor.isActive('heading', { level: 1 })) {
    return heading1Option;
  } else if (editor.isActive('heading', { level: 2 })) {
    return heading2Option;
  } else if (editor.isActive('heading', { level: 3 })) {
    return heading3Option;
  } else {
    return null;
  }
};

interface IProps {
  editor: Editor;
}

const reactSelectStyles: StylesConfig<IReactSelectOption, false, never> = {
  control: (base) => ({ ...base, minWidth: '90px' }),
  singleValue: (base) => ({
    ...base,
    color: theme.colors.mainBlue,
    padding: '0 0 0 6px',
  }),
};

const reactSelectComponents: SelectComponentsConfig<
  IReactSelectOption,
  false,
  never
> = {
  DropdownIndicator: (props) =>
    BasicDropdownIndicator({ props, color: theme.colors.mainBlue }),
  SingleValue: ReactSelectComponents.SingleValue,
};

/**
 * Select block style (heading, paragraph) using React Select
 */
export const BlockStyleSelect = ({ editor }: IProps) => {
  const selectedBlockStyle = getSelectedBlockStyle(editor);

  const handleBlockStyleChange = (
    newValue: SingleValue<IReactSelectOption>,
  ) => {
    if (isNil(newValue)) {
      return;
    }

    if (newValue.value === 'paragraph') {
      editor.chain().focus().setParagraph().run();
    } else if (newValue.value === 'heading-1') {
      editor.chain().focus().setHeading({ level: 1 }).run();
    } else if (newValue.value === 'heading-2') {
      editor.chain().focus().setHeading({ level: 2 }).run();
    }
  };

  return (
    <ReactSelectMenu
      components={reactSelectComponents}
      isClearable={false}
      isDisabled={!editor.isEditable}
      isSearchable={false}
      onChange={handleBlockStyleChange}
      options={blockStyleOptions}
      styles={reactSelectStyles}
      value={selectedBlockStyle}
    />
  );
};
